import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Copy } from "lucide-react";
import api from "../../../../../utils/axiosInstance";
import { exportTable } from "../../../../../utils/exportTable";
import { toast, confirmDialog } from "../../../../../components/Toast/toast";
import { formatDateTime, formatLanguagePair, formatMoney } from "../../quotationFormat";
import { btn } from "../../../../../components/Button/buttonStyles";

const TYPE_BADGE = {
  flat_rate: { label: "Flat", cls: "bg-amber-100 text-amber-700", rail: "border-l-2 border-amber-400" },
  unit_based: { label: "Unit", cls: "bg-teal-100 text-teal-700", rail: "border-l-2 border-teal-400" },
};

const isUnit = (r) => r.type === "unit_based";
const orDash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const EXPORT_COLUMNS = [
  { header: "Type", value: (r) => TYPE_BADGE[r.type].label },
  { header: "Service", value: (r) => r.service?.name || "" },
  { header: "Language Pair", value: (r) => formatLanguagePair(r.languagePair) },
  { header: "Unit Amount", value: (r) => (isUnit(r) ? r.unit_amount : "") },
  { header: "Unit", value: (r) => (isUnit(r) ? r.unit?.name || "" : "") },
  { header: "Price / Unit", value: (r) => (isUnit(r) ? r.price_per_unit : "") },
  { header: "Subtotal", value: (r) => Number(r.subtotal).toFixed(2) },
  { header: "Currency", value: (r) => r.currency?.currency?.code || "" },
  { header: "File", value: (r) => r.file?.original_file_name || "" },
  { header: "Internal Note", value: (r) => r.internal_note || "" },
  { header: "Created At", value: (r) => formatDateTime(r.createdAt) },
];

const HEADERS = [
  "Type",
  "Service",
  "Language Pair",
  "Unit Amount",
  "Unit",
  "Price / Unit",
  "Subtotal",
  "Currency",
  "File",
  "Internal Note",
  "Created At",
  "Actions",
];

// Fields a clone carries over; the server recomputes unit-based subtotals.
const clonePayload = (r) => ({
  type: r.type,
  service_id: r.service_id,
  language_pair_id: r.language_pair_id,
  currency_id: r.currency_id,
  file_id: r.file_id,
  internal_note: r.internal_note,
  ...(isUnit(r)
    ? { unit_amount: Number(r.unit_amount), unit_id: r.unit_id, price_per_unit: Number(r.price_per_unit) }
    : { subtotal: Number(r.subtotal) }),
});

const FinancesTab = ({ quotationId, locked }) => {
  const navigate = useNavigate();
  const [receivables, setReceivables] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [receivablesRes, taxesRes] = await Promise.all([
        api.get(`/quotations/${quotationId}/receivables`),
        api.get(`/quotations/${quotationId}/taxes`),
      ]);
      setReceivables(receivablesRes.data.data || []);
      setTotals(taxesRes.data.meta);
    } catch (err) {
      console.error("Failed to fetch receivables", err);
      toast.error("Failed to fetch receivables");
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async (row) => {
    if (!(await confirmDialog("Delete this receivable?"))) return;
    try {
      await api.delete(`/quotations/${quotationId}/receivables/${row.id}`);
      fetchData();
    } catch (err) {
      console.error("Delete failed", err);
      toast.error(err.response?.data?.message || "Failed to delete receivable");
    }
  };

  const handleClone = async (row) => {
    try {
      await api.post(`/quotations/${quotationId}/receivables`, clonePayload(row));
      fetchData();
    } catch (err) {
      console.error("Clone receivable failed", err);
      toast.error(err.response?.data?.message || "Failed to clone receivable");
    }
  };

  const goToNew = (type) => {
    setMenuOpen(false);
    navigate(`/quotation/${quotationId}/new-receivable?type=${type}`);
  };

  const currency = totals?.currency ? ` ${totals.currency}` : "";

  return (
    <div className="space-y-5">
      <section>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-gray-900">Receivables from Client</h2>
          <div className="flex items-center gap-2 text-sm">
            {!locked && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className={btn("primary", "sm")}
                >
                  New Receivable <span className="text-xs">▾</span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    <button onClick={() => goToNew("flat_rate")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100">
                      Flat Rate Receivable
                    </button>
                    <button onClick={() => goToNew("unit_based")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100">
                      Unit Based Receivable
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => exportTable(`quotation-${quotationId}-receivables`, EXPORT_COLUMNS, receivables)}
              className={btn("secondary", "sm")}
            >
              Export to Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
          </div>
        ) : (
          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-3 rounded-sm bg-amber-400" />
                Flat Rate
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-3 rounded-sm bg-teal-400" />
                Unit Based
              </span>
            </div>

            <div className="max-h-[460px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {receivables.length === 0 ? (
                    <tr>
                      <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                        No receivables found.
                      </td>
                    </tr>
                  ) : (
                    receivables.map((row, index) => {
                      const badge = TYPE_BADGE[row.type];
                      const unit = isUnit(row);
                      return (
                        <tr
                          key={row.id}
                          className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} ${badge.rail} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-center whitespace-nowrap">
                            <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs break-words">{orDash(row.service?.name)}</td>
                          <td className="px-3 py-2 text-xs break-words">{orDash(formatLanguagePair(row.languagePair))}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap">{unit ? row.unit_amount : "—"}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{unit ? orDash(row.unit?.name) : "—"}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap">{unit ? row.price_per_unit : "—"}</td>
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">{formatMoney(row.subtotal)}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{orDash(row.currency?.currency?.code)}</td>
                          <td className="px-3 py-2 text-xs break-words">{orDash(row.file?.original_file_name)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 break-words">{orDash(row.internal_note)}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">
                            <div className="flex justify-center gap-3">
                              {locked ? (
                                <span className="text-gray-300">—</span>
                              ) : (
                                <>
                                  <Copy
                                    className="w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
                                    title="Clone"
                                    onClick={() => handleClone(row)}
                                  />
                                  <Pencil
                                    className="w-4 h-4 text-brand-400 hover:text-brand-600 cursor-pointer transition-colors"
                                    title="Edit"
                                    onClick={() => navigate(`/quotation/${quotationId}/receivable/${row.id}/edit`)}
                                  />
                                  <Trash2
                                    className="w-4 h-4 text-danger-400 hover:text-danger-600 cursor-pointer transition-colors"
                                    title="Delete"
                                    onClick={() => handleDelete(row)}
                                  />
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {totals && (
        <section className="flex justify-end">
          <div className="w-full sm:w-80 bg-white shadow rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>{formatMoney(totals.subtotal)}{currency}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Taxes</span>
              <span>{formatMoney(totals.tax_total)}{currency}</span>
            </div>
            <div className="flex justify-between border-t pt-2 font-semibold text-gray-900">
              <span>Total</span>
              <span>{formatMoney(totals.total)}{currency}</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default FinancesTab;
